import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { AuthShell } from "./Signup";
import { Loader2 } from "lucide-react";
import PasswordInput from "../components/PasswordInput";

/**
 * ResetPassword — the only surface where users can complete a password
 * recovery. Access rules:
 *
 *  - Reached via the email recovery link → Supabase fires PASSWORD_RECOVERY
 *    → AuthContext sets `recoveryRequired = true` (persisted in
 *    sessionStorage). ProtectedRoute forwards signed-in users here.
 *
 *  - Reached without a recovery flow AND no session → send them to /login.
 *
 *  - Reached without a recovery flow BUT with an existing session (curious
 *    user navigating manually) → send them to /app. No password change.
 *
 *  - On success: AuthContext.updatePassword clears the recoveryRequired
 *    flag → ProtectedRoute lets them into /app.
 */
export default function ResetPassword() {
  const { updatePassword, recoveryRequired, session, loading } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);

  // Give Supabase a moment to process the recovery hash on first mount.
  // If after auth loads we still don't have a recovery flow, bounce away.
  // Skip while `ok` (post-success confirmation is showing) so the success
  // toast renders before we navigate.
  useEffect(() => {
    if (loading) return;
    if (ok) return;
    if (recoveryRequired) return;
    if (!session) {
      navigate("/login", { replace: true });
    } else {
      // Signed-in user manually navigated here — nothing to do, send home.
      navigate("/app", { replace: true });
    }
  }, [loading, recoveryRequired, session, ok, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    const { error: err } = await updatePassword(password);
    if (err) {
      setError(err.message);
      setBusy(false);
      return;
    }
    setOk(true);
    // Brief confirmation, then head to the app. The recovery flag was
    // cleared by updatePassword so ProtectedRoute will allow entry.
    setTimeout(() => navigate("/app", { replace: true }), 900);
  };

  if (loading || (!recoveryRequired && !ok)) {
    return (
      <AuthShell title="Loading…" subtitle="">
        <div className="flex justify-center py-8" data-testid="reset-loading">
          <Loader2 className="w-6 h-6 animate-spin text-rose" />
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set a new password" subtitle="Choose something memorable and strong.">
      <form onSubmit={submit} noValidate className="space-y-3" data-testid="reset-form">
        <PasswordInput
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="New password"
          autoComplete="new-password"
          minLength={8}
          testId="reset-password"
          className="rounded-full bg-white border border-cocoa/10 px-5 py-3.5 text-sm placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40"
        />
        <PasswordInput
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirm new password"
          autoComplete="new-password"
          minLength={8}
          testId="reset-password-confirm"
          className="rounded-full bg-white border border-cocoa/10 px-5 py-3.5 text-sm placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40"
        />
        {error && (
          <p className="text-sm text-red-500 font-light px-2" data-testid="reset-error">
            {error}
          </p>
        )}
        {ok && (
          <p className="text-sm text-emerald-600 font-light px-2" data-testid="reset-success">
            Password updated — signing you in…
          </p>
        )}
        <button
          type="submit"
          disabled={busy || ok}
          data-testid="reset-submit"
          className="w-full rounded-full bg-rose hover:bg-rose-dark text-white py-3.5 font-medium transition-all shadow-rose flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
        </button>
        <p className="text-[11px] text-cocoa/50 text-center pt-1 font-light">
          Must be at least 8 characters.
        </p>
      </form>
    </AuthShell>
  );
}
